import {useEffect, useMemo, useRef, useState} from 'react'
import {Outlet, Link as RouterLink, useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Box, IconButton, Drawer, List, ListItemButton, ListItemText, Divider, useMediaQuery} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import {useAuth} from '../auth/useAuth'
import {useMe} from '../../common/hooks/useMe'
import {trackEvent} from '../../common/analytics/trackEvent'
import {trackPageView} from '../../common/analytics/ga'
import {isApiError} from '../../common/errors/ApiError'
import {useAppTokens, type ThemeTokens} from '../theme'
import BrandLogo from '../components/BrandLogo'
import Footer from '../components/Footer'
import NavAvatar from './NavAvatar'
import NavToolsMenu from './NavToolsMenu'
import {TOOL_LINKS} from '../../common/constants/toolLinks'

const MOBILE_BREAKPOINT = '(max-width: 759px)'
// Reserved space on the left to keep the main logo visible when the drawer opens.
// = nav padding-left (20) + logo mark (24) + gap (10) + wordmark (~95) + breathing room (~15)
const LOGO_ZONE_WIDTH = 50

const PublicLayout = () => {
  const {user, signOut} = useAuth()
  const {me, error: meError} = useMe()
  const navigate = useNavigate()
  const location = useLocation()
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)
  const [menuOpen, setMenuOpen] = useState(false)
  const isAccountActive = location.pathname === '/account'

  // GA's automatic pageview only covers the URL present at init (a direct/full-page landing);
  // client-side route changes need an explicit event. Skip the first location — that one is
  // already covered by GA's `send_page_view` init pageview, so firing here too would double-count.
  const isFirstLocation = useRef(true)
  useEffect(() => {
    if (isFirstLocation.current) {
      isFirstLocation.current = false
      return
    }
    // Deferred one frame so react-helmet-async's title update (a page's own effect, which runs
    // before this parent effect) has committed before we read document.title.
    const raf = requestAnimationFrame(() => trackPageView(`${location.pathname}${location.search}`))
    return () => cancelAnimationFrame(raf)
  }, [location.pathname, location.search])

  // Fatal escalation: user is signed in but /me is unreachable with a non-401 error.
  // 401 is expected when the token expires (handled by app:auth-expired → sign-out).
  // 5xx/network on /me means the app's auth context is broken — show SorryPage instead
  // of a half-rendered shell with no profile menu.
  useEffect(() => {
    if (!user || !meError || me) return
    if (isApiError(meError) && meError.status === 401) return
    if (location.pathname === '/error') return
    navigate('/error', {
      replace: true,
      state: {
        error: meError instanceof Error ? meError.message : String(meError),
        returnTo: `${location.pathname}${location.search}`,
      },
    })
  }, [user, me, meError, location.pathname, location.search, navigate])

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    navigate('/login')
  }

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const hideFooter = location.pathname.startsWith('/login')

  // Split around the Tools dropdown (rendered separately, not a plain link) so
  // the desktop nav and mobile drawer can both place it between Projects and Pricing.
  const publicLinksBeforeTools = [
    {to: '/features', label: t('landing.nav.features')},
    {to: '/projects', label: t('landing.nav.projects')},
  ]
  const publicLinksAfterTools = [
    {to: '/pricing', label: user ? t('landing.nav.buyCredits') : t('landing.nav.pricing')},
    {to: '/about', label: t('landing.nav.about')},
  ]

  const authedLinks = user
    ? [
        {to: '/my-projects', label: t('auth.myProjects')},
        {to: '/render-history', label: t('auth.renderHistory')},
      ]
    : [{to: '/login', label: t('auth.logIn')}]

  const closeMenu = () => setMenuOpen(false)

  return (
    <Box sx={sx.root}>
      <Box sx={sx.bgGlow} />

      <Box component="nav" sx={sx.nav}>
        <Box sx={sx.navLeft}>
          <RouterLink to="/" style={{textDecoration: 'none', marginRight: 12}}>
            <BrandLogo markSize={48} fontSize={19} gap={12} />
          </RouterLink>
          {!isMobile && (
            <>
              {publicLinksBeforeTools.map((link) => (
                <Box key={link.to} component={RouterLink} to={link.to} sx={sx.link}>
                  {link.label}
                </Box>
              ))}
              <NavToolsMenu />
              {publicLinksAfterTools.map((link) => (
                <Box key={link.to} component={RouterLink} to={link.to} sx={sx.link}>
                  {link.label}
                </Box>
              ))}
            </>
          )}
        </Box>

        {!isMobile && (
          <Box sx={sx.navRight}>
            {user && (
              <Box component={RouterLink} to="/editor/new" sx={sx.navCta}>
                <Box component="span" sx={sx.navCtaPlus}>
                  +
                </Box>
                {t('landing.nav.newProject')}
              </Box>
            )}
            {authedLinks.map((link) => (
              <Box key={link.to} component={RouterLink} to={link.to} sx={sx.link}>
                {link.label}
              </Box>
            ))}
            {user && me && <NavAvatar me={me} active={isAccountActive} />}
          </Box>
        )}

        {isMobile && (
          <IconButton
            aria-label={t('landing.nav.openMenu')}
            onClick={() => {
              setMenuOpen(true)
              trackEvent('mobile_menu_opened')
            }}
            sx={sx.menuButton}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={closeMenu}
        disableScrollLock
        slotProps={{paper: {sx: sx.drawerPaper}}}
      >
        <Box sx={sx.drawerHeader}>
          <BrandLogo markSize={28} fontSize={16} gap={10} />
          <IconButton aria-label={t('landing.nav.closeMenu')} onClick={closeMenu} sx={sx.menuButton}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={sx.drawerList}>
          {user && (
            <ListItemButton component={RouterLink} to="/editor/new" onClick={closeMenu} sx={sx.drawerItemCta}>
              <ListItemText
                primary={`+ ${t('landing.nav.newProject')}`}
                slotProps={{primary: {sx: sx.drawerItemTextCta}}}
              />
            </ListItemButton>
          )}
          {publicLinksBeforeTools.map((link) => (
            <ListItemButton key={link.to} component={RouterLink} to={link.to} onClick={closeMenu} sx={sx.drawerItem}>
              <ListItemText primary={link.label} slotProps={{primary: {sx: sx.drawerItemText}}} />
            </ListItemButton>
          ))}
          <ListItemText
            primary={t('landing.nav.tools')}
            sx={sx.drawerGroupLabel}
            slotProps={{primary: {sx: sx.drawerGroupLabelText}}}
          />
          {TOOL_LINKS.map((link) => (
            <ListItemButton key={link.to} component={RouterLink} to={link.to} onClick={closeMenu} sx={sx.drawerItem}>
              <ListItemText primary={t(link.labelKey)} slotProps={{primary: {sx: sx.drawerItemText}}} />
            </ListItemButton>
          ))}
          {publicLinksAfterTools.map((link) => (
            <ListItemButton key={link.to} component={RouterLink} to={link.to} onClick={closeMenu} sx={sx.drawerItem}>
              <ListItemText primary={link.label} slotProps={{primary: {sx: sx.drawerItemText}}} />
            </ListItemButton>
          ))}
          <Divider sx={sx.drawerDivider} />
          {authedLinks.map((link) => (
            <ListItemButton key={link.to} component={RouterLink} to={link.to} onClick={closeMenu} sx={sx.drawerItem}>
              <ListItemText primary={link.label} slotProps={{primary: {sx: sx.drawerItemText}}} />
            </ListItemButton>
          ))}
          {user && (
            <ListItemButton component={RouterLink} to="/account" onClick={closeMenu} sx={sx.drawerItem}>
              <ListItemText
                primary={t('account.headerMenu.accountSettings')}
                slotProps={{primary: {sx: sx.drawerItemText}}}
              />
            </ListItemButton>
          )}
          {user && (
            <ListItemButton onClick={handleSignOut} sx={sx.drawerItem}>
              <ListItemText primary={t('auth.signOut')} slotProps={{primary: {sx: sx.drawerItemText}}} />
            </ListItemButton>
          )}
        </List>
      </Drawer>

      <Box component="main" sx={sx.main}>
        <Outlet />
      </Box>

      {!hideFooter && <Footer />}
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    position: 'relative' as const,
    minHeight: '100vh',
    background: tokens.surface.background,
    color: tokens.text.primary,
    overflowX: 'hidden' as const,
  },
  bgGlow: {
    position: 'fixed' as const,
    inset: 0,
    pointerEvents: 'none' as const,
    background: `
      radial-gradient(ellipse 1200px 700px at 50% -10%, ${tokens.accent.glow}, transparent 60%),
      radial-gradient(ellipse 800px 600px at 85% 30%, rgba(167, 139, 250, 0.08), transparent 60%)
    `,
    zIndex: 0,
  },
  nav: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: {xs: '14px 20px', md: '18px 32px'},
    backdropFilter: 'blur(18px) saturate(140%)',
    WebkitBackdropFilter: 'blur(18px) saturate(140%)',
    background: 'linear-gradient(180deg, rgba(7, 6, 11, 0.85), rgba(7, 6, 11, 0.4))',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    // xs must be set explicitly — MUI's responsive sx only emits `gap` inside
    // the breakpoints listed, so without a base value the desktop nav rendered
    // with ZERO spacing between 760px (mobile drawer cutoff) and 900px (`md`),
    // squeezing every link together. See docs/frontend.md nav responsive note.
    gap: {xs: '18px', md: '28px', lg: '32px'},
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  link: {
    color: tokens.text.secondary,
    fontSize: '14px',
    fontWeight: 450,
    textDecoration: 'none',
    transition: 'color 0.2s',
    '&:hover': {color: tokens.text.primary},
  },
  navCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '32px',
    padding: '0 12px 0 10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '-0.005em',
    color: tokens.text.primary,
    background: 'linear-gradient(180deg, rgba(124, 92, 255, 0.35) 0%, rgba(124, 92, 255, 0.18) 100%)',
    border: '1px solid rgba(124, 92, 255, 0.5)',
    boxShadow: '0 0 0 1px rgba(124, 92, 255, 0.1) inset, 0 8px 22px -10px rgba(124, 92, 255, 0.7)',
    textDecoration: 'none',
    transition: 'transform 0.15s, box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: 'rgba(124, 92, 255, 0.7)',
      boxShadow: '0 12px 28px -10px rgba(124, 92, 255, 0.8)',
    },
  },
  navCtaPlus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '5px',
    background: 'rgba(124, 92, 255, 0.6)',
    color: '#fff',
    fontSize: '14px',
    lineHeight: 1,
  },
  menuButton: {
    color: tokens.text.primary,
    border: `1px solid ${tokens.accordion.borderStrong}`,
    borderRadius: '8px',
    width: 40,
    height: 40,
    '&:hover': {background: 'rgba(255, 255, 255, 0.04)'},
  },
  drawerPaper: {
    background: tokens.surface.background,
    color: tokens.text.primary,
    borderLeft: `1px solid ${tokens.accordion.border}`,
    width: `calc(100vw - ${LOGO_ZONE_WIDTH}px)`,
    backgroundImage: 'none',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
  drawerList: {
    padding: '12px 12px',
  },
  drawerItem: {
    borderRadius: '8px',
    padding: '12px 14px',
    color: tokens.text.secondary,
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.04)',
      color: tokens.text.primary,
    },
  },
  drawerItemText: {
    fontSize: '15px',
    fontWeight: 500,
  },
  drawerGroupLabel: {
    padding: '14px 14px 4px',
  },
  drawerGroupLabelText: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: tokens.text.muted,
  },
  drawerItemCta: {
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '4px',
    color: tokens.text.primary,
    background: 'linear-gradient(180deg, rgba(124, 92, 255, 0.35) 0%, rgba(124, 92, 255, 0.18) 100%)',
    border: '1px solid rgba(124, 92, 255, 0.5)',
    boxShadow: '0 0 0 1px rgba(124, 92, 255, 0.1) inset, 0 8px 22px -10px rgba(124, 92, 255, 0.7)',
    transition: 'background 0.15s, border-color 0.15s',
    '&:hover': {
      background: 'linear-gradient(180deg, rgba(124, 92, 255, 0.45) 0%, rgba(124, 92, 255, 0.25) 100%)',
      borderColor: 'rgba(124, 92, 255, 0.7)',
    },
  },
  drawerItemTextCta: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.text.primary,
  },
  drawerDivider: {
    margin: '8px 4px',
    borderColor: tokens.accordion.border,
  },
  main: {
    position: 'relative' as const,
    zIndex: 2,
  },
})

export default PublicLayout
